/**
 * Teen microsite — loaded only on Teen suffixes after theme.js.
 * Idempotent init for DOM ready + Theme Editor section:load.
 */
(function () {
  'use strict';

  if (!document.body || !document.body.classList.contains('template-teen')) {
    return;
  }

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var catObserver = null;
  var catShowTimer = null;
  var heroCatObserver = null;
  var heroCatTimer = null;
  var fabricObserver = null;
  var dogUpdateHandler = null;
  var faqScrollHandler = null;
  var faqResizeHandler = null;
  var faqClickBound = false;

  function initStickyOffset() {
    var header =
      document.querySelector('.site-header') ||
      document.getElementById('SiteHeader') ||
      document.querySelector('[data-section-type="header"]');
    if (!header) return;

    var height = header.offsetHeight || 0;
    // The token is declared on body.template-teen, so set the runtime value
    // there as well. The announcement bar scrolls away and must not be counted.
    document.body.style.setProperty('--teen-sticky-offset', height + 'px');

    // Hero tagline fills the first screen: subtract everything above MainContent
    // (announcement + header) so the product grid stays below the fold.
    var announcement = document.querySelector('.announcement-bar');
    var heroOffset = height + (announcement ? announcement.offsetHeight || 0 : 0);
    document.body.style.setProperty('--teen-hero-offset', heroOffset + 'px');
  }

  function getStickyLine() {
    var raw = getComputedStyle(document.body).getPropertyValue(
      '--teen-sticky-offset'
    );
    var offset = parseFloat(raw) || 0;
    return offset + 24;
  }

  function initFaqNav() {
    var nav = document.querySelector('[data-teen-faq-nav]');
    if (!nav) return;

    var links = Array.prototype.slice.call(
      nav.querySelectorAll('[data-teen-faq-nav-link]')
    );
    var sections = [];

    links.forEach(function (link) {
      var id = (link.getAttribute('href') || '').replace(/^#/, '');
      var section = id && document.getElementById(id);
      if (section) sections.push(section);
    });
    if (!sections.length) return;

    function setActive(id) {
      links.forEach(function (link) {
        var on = (link.getAttribute('href') || '').replace(/^#/, '') === id;
        link.classList.toggle('is-active', on);
        if (on) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    }

    // Delegate at document level: the Theme Editor replaces the section
    // node on section:load, which would orphan a nav-bound listener
    if (!faqClickBound) {
      faqClickBound = true;
      document.addEventListener('click', function (e) {
        var link = e.target.closest
          ? e.target.closest('[data-teen-faq-nav-link]')
          : null;
        if (!link) return;
        var id = (link.getAttribute('href') || '').replace(/^#/, '');
        var section = document.getElementById(id);
        if (!section) return;
        e.preventDefault();
        section.scrollIntoView({
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
          block: 'start'
        });
        if (history.replaceState) {
          history.replaceState(null, '', '#' + id);
        }
      });
    }

    var activeId = null;

    function syncActive() {
      var line = getStickyLine();
      var currentId = sections[0].id;
      sections.forEach(function (section) {
        if (section.getBoundingClientRect().top <= line) {
          currentId = section.id;
        }
      });
      if (currentId !== activeId) {
        activeId = currentId;
        setActive(currentId);
      }
    }

    if (faqScrollHandler) {
      window.removeEventListener('scroll', faqScrollHandler);
    }
    if (faqResizeHandler) {
      window.removeEventListener('resize', faqResizeHandler);
    }
    faqScrollHandler = syncActive;
    faqResizeHandler = syncActive;
    window.addEventListener('scroll', syncActive, { passive: true });
    window.addEventListener('resize', syncActive, { passive: true });
    syncActive();
  }

  function initHeroCat() {
    if (heroCatTimer) {
      window.clearTimeout(heroCatTimer);
      heroCatTimer = null;
    }
    if (heroCatObserver) {
      heroCatObserver.disconnect();
      heroCatObserver = null;
    }

    var cat = document.querySelector('[data-teen-hero-cat]');
    var hero =
      document.querySelector('[data-teen-hero-tagline]') ||
      (cat && cat.closest('.teen-tagline--hero'));
    if (!cat || !hero) return;

    var showDelayMs = 600;

    function setVisible(on) {
      cat.classList.toggle('is-visible', on);
    }

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }

    setVisible(false);

    heroCatObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            if (cat.classList.contains('is-visible') || heroCatTimer) return;
            heroCatTimer = window.setTimeout(function () {
              heroCatTimer = null;
              setVisible(true);
            }, showDelayMs);
          } else {
            if (heroCatTimer) {
              window.clearTimeout(heroCatTimer);
              heroCatTimer = null;
            }
            setVisible(false);
          }
        });
      },
      { threshold: 0.15 }
    );
    heroCatObserver.observe(hero);
  }

  function initFooterCat() {
    var cat = document.querySelector('[data-teen-cat]');
    var footer = document.querySelector('.site-footer');
    if (!cat || !footer) return;

    var showDelayMs = 600;

    function setVisible(on) {
      cat.classList.toggle('is-visible', on);
    }

    function clearShowTimer() {
      if (catShowTimer) {
        window.clearTimeout(catShowTimer);
        catShowTimer = null;
      }
    }

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      clearShowTimer();
      setVisible(true);
      return;
    }

    if (catObserver) {
      catObserver.disconnect();
      catObserver = null;
    }
    clearShowTimer();

    catObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            if (cat.classList.contains('is-visible') || catShowTimer) return;
            catShowTimer = window.setTimeout(function () {
              catShowTimer = null;
              setVisible(true);
            }, showDelayMs);
          } else {
            clearShowTimer();
            setVisible(false);
          }
        });
      },
      { threshold: 0.15 }
    );
    catObserver.observe(footer);
  }

  function initHeroDogReveal() {
    var section = document.querySelector('[data-teen-pdp]');
    var dog = document.querySelector('[data-teen-hero-dog] .teen-hero-dog__img');
    if (!section || !dog) return;
    if (section.getAttribute('data-teen-dog-bound') === 'true' && dogUpdateHandler) {
      return;
    }

    // Theme Editor section:load replaces the section node; drop listeners
    // bound against the old detached DOM before binding fresh ones
    if (dogUpdateHandler) {
      window.removeEventListener('scroll', dogUpdateHandler);
      window.removeEventListener('resize', dogUpdateHandler);
      dogUpdateHandler = null;
    }
    section.setAttribute('data-teen-dog-bound', 'true');

    function isDogBottomInView() {
      var rect = dog.getBoundingClientRect();
      return rect.bottom > 0 && rect.bottom <= window.innerHeight;
    }

    function update() {
      if (reducedMotion.matches) {
        section.classList.add('is-teen-dog-revealed');
        return;
      }
      section.classList.toggle('is-teen-dog-revealed', isDogBottomInView());
    }

    dogUpdateHandler = update;
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  function initFabricDogReveal() {
    var panel = document.querySelector('[data-teen-fabric]');
    var dog = document.querySelector('[data-teen-fabric-dog]');
    if (!panel || !dog) return;

    function setRevealed(on) {
      panel.classList.toggle('is-teen-fabric-revealed', on);
    }

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      setRevealed(true);
      return;
    }

    if (fabricObserver) {
      fabricObserver.disconnect();
      fabricObserver = null;
    }

    fabricObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          setRevealed(entry.isIntersecting);
        });
      },
      { threshold: 0.2 }
    );
    fabricObserver.observe(panel);
  }

  function init() {
    initStickyOffset();
    initHeroCat();
    initFooterCat();
    initHeroDogReveal();
    initFabricDogReveal();
    initFaqNav();
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  onReady(init);

  document.addEventListener('shopify:section:load', init);
  window.addEventListener('resize', initStickyOffset);

  window.TeenMicrosite = window.TeenMicrosite || {
    init: init,
    initStickyOffset: initStickyOffset,
    prefersReducedMotion: function () {
      return reducedMotion.matches;
    }
  };
})();
